<template>
  <Layout>
    <div class="about-page">
      <!-- Hero Section -->
      <section class="about-hero bg-gradient-primary text-white py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-6">
              <h1 class="display-4 font-weight-bold mb-4">Your Journey to Holistic Beauty</h1>
              <p class="lead mb-4">Wellness and healthcare—all in one place.</p>
              <b-button variant="light" size="lg" href="#our-story">
                <b-icon icon="arrow-down" class="mr-2"></b-icon>
                Discover Our Story
              </b-button>
            </div>
            <div class="col-lg-6 d-none d-lg-block">
              <div class="hero-illustration">
                <img src="@/assets/images/about-hero.png" alt="OVVURA Team" class="img-fluid rounded-lg shadow-lg">
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Our Story Section -->
      <section id="our-story" class="py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-6 mb-4 mb-lg-0">
              <div class="story-image">
                <img src="@/assets/images/about-story.jpg" alt="Our Story" class="img-fluid rounded-lg shadow">
              </div>
            </div>
            <div class="col-lg-6">
              <h2 class="font-weight-bold mb-4">Our Story</h2>
              <p class="lead text-muted mb-4">Founded in 2024 and headquartered in Dubai, OVVURA PORTAL L.L.C launched with a single mission: to unlock your radiance.</p>
              <p>We began as a small team of beauty and wellness enthusiasts who saw a gap between traditional spa appointments and modern, on-demand convenience. Today, we're proud to serve customers across the United Arab Emirates and GCC Countries, connecting you with licensed professionals and handpicked self-care products.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Mission & Vision -->
      <section class="py-5 bg-light">
        <div class="container">
          <div class="row">
            <div class="col-lg-6 mb-4 mb-lg-0">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body p-4">
                  <div class="icon-circle bg-primary text-white mb-3">
                    <i class="mdi mdi-bullseye-arrow" style="font-size: 1.5rem;"></i>
                  </div>
                  <h3 class="font-weight-bold mb-3">Our Mission</h3>
                  <p class="mb-0">To make professional beauty, healthcare, and wellness services accessible to every individual—anytime, anywhere.</p>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body p-4">
                  <div class="icon-circle bg-primary text-white mb-3">
                    <i class="mdi mdi-eye-outline" style="font-size: 1.5rem;"></i>
                  </div>
                  <h3 class="font-weight-bold mb-3">Our Vision</h3>
                  <p class="mb-0">We envision a world where self-care is simple, personalized, and seamlessly integrated into your daily life.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- What We Offer -->
      <section class="py-5">
        <div class="container">
          <div class="text-center mb-5">
            <h2 class="font-weight-bold">What We Offer</h2>
            <p class="lead text-muted">Comprehensive solutions for your beauty, wellness, and healthcare needs</p>
          </div>
          
          <div class="row">
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center p-4">
                  <div class="icon-circle-lg bg-primary text-white mb-3 mx-auto">
                    <i class="mdi mdi-calendar-check" style="font-size: 1.5rem;"></i>
                  </div>
                  <h4 class="font-weight-bold mb-3">Online Booking</h4>
                  <p>Spa treatments, dental check-ups, haircuts, makeup artistry, Aerobics, Gym, yoga, and more.</p>
                </div>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center p-4">
                  <div class="icon-circle-lg bg-primary text-white mb-3 mx-auto">
                    <i class="mdi mdi-cart-outline" style="font-size: 1.5rem;"></i>
                  </div>
                  <h4 class="font-weight-bold mb-3">Curated E-Commerce</h4>
                  <p>A selection of beauty, wellness, and self-care products vetted for quality and efficacy.</p>
                </div>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center p-4">
                  <div class="icon-circle-lg bg-primary text-white mb-3 mx-auto">
                    <i class="mdi mdi-home-heart" style="font-size: 1.5rem;"></i>
                  </div>
                  <h4 class="font-weight-bold mb-3">At-Home Services</h4>
                  <p>Book a licensed professional to visit you at home, office, or hotel—on your schedule.</p>
                </div>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body text-center p-4">
                  <div class="icon-circle-lg bg-primary text-white mb-3 mx-auto">
                    <i class="mdi mdi-map-marker-radius" style="font-size: 1.5rem;"></i>
                  </div>
                  <h4 class="font-weight-bold mb-3">Nationwide Coverage</h4>
                  <p>Serving Dubai, Abu Dhabi, Sharjah, and all UAE emirates as well as GCC with trusted partners.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Our Values -->
      <section class="py-5 bg-light">
        <div class="container">
          <div class="text-center mb-5">
            <h2 class="font-weight-bold">Our Values</h2>
            <p class="lead text-muted">The principles that guide everything we do</p>
          </div>
          
          <div class="row">
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="value-card p-4 h-100">
                <div class="value-icon mb-3">
                  <i class="mdi mdi-star-circle text-primary" style="font-size: 2rem;"></i>
                </div>
                <h4 class="font-weight-bold mb-3">Quality First</h4>
                <p>Only licensed, vetted professionals and premium products make it onto our platform.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="value-card p-4 h-100">
                <div class="value-icon mb-3">
                  <i class="mdi mdi-account-heart text-primary" style="font-size: 2rem;"></i>
                </div>
                <h4 class="font-weight-bold mb-3">Customer Centric</h4>
                <p>Your safety, convenience, and satisfaction guide every decision we make.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="value-card p-4 h-100">
                <div class="value-icon mb-3">
                  <i class="mdi mdi-lightbulb-on text-primary" style="font-size: 2rem;"></i>
                </div>
                <h4 class="font-weight-bold mb-3">Innovation</h4>
                <p>We continuously refine our app and website to streamline booking and discovery.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="value-card p-4 h-100">
                <div class="value-icon mb-3">
                  <i class="mdi mdi-shield-check text-primary" style="font-size: 2rem;"></i>
                </div>
                <h4 class="font-weight-bold mb-3">Integrity</h4>
                <p>Transparent pricing, secure payments, and clear policies keep your trust front and center.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Why Choose Us -->
      <section class="py-5">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-6 mb-4 mb-lg-0">
              <h2 class="font-weight-bold mb-4">Why OVVURA?</h2>
              <ul class="list-unstyled why-list">
                <li class="mb-3">
                  <div class="d-flex">
                    <div class="mr-3">
                      <i class="mdi mdi-check-circle text-primary" style="font-size: 1.5rem;"></i>
                    </div>
                    <div>
                      <h5 class="font-weight-bold">All-in-One Platform</h5>
                      <p class="mb-0">Beauty, healthcare, and wellness—all bookable in under a minute.</p>
                    </div>
                  </div>
                </li>
                <li class="mb-3">
                  <div class="d-flex">
                    <div class="mr-3">
                      <i class="mdi mdi-check-circle text-primary" style="font-size: 1.5rem;"></i>
                    </div>
                    <div>
                      <h5 class="font-weight-bold">Secure Payments</h5>
                      <p class="mb-0">Visa, MasterCard, UnionPay and American Express are accepted in AED.</p>
                    </div>
                  </div>
                </li>
                <li class="mb-3">
                  <div class="d-flex">
                    <div class="mr-3">
                      <i class="mdi mdi-check-circle text-primary" style="font-size: 1.5rem;"></i>
                    </div>
                    <div>
                      <h5 class="font-weight-bold">Trusted Partners</h5>
                      <p class="mb-0">We do not serve sanctioned regions and our providers meet strict licensing standards.</p>
                    </div>
                  </div>
                </li>
                <li class="mb-3">
                  <div class="d-flex">
                    <div class="mr-3">
                      <i class="mdi mdi-check-circle text-primary" style="font-size: 1.5rem;"></i>
                    </div>
                    <div>
                      <h5 class="font-weight-bold">Local Roots, Global Standards</h5>
                      <p class="mb-0">Domiciled in the UAE, governed by UAE law, we combine local expertise with world-class service.</p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <div class="col-lg-6">
              <div class="why-image">
                <img src="@/assets/images/about-why.jpg" alt="Why Choose Us" class="img-fluid rounded-lg shadow">
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Safety & Sustainability -->
      <section class="py-5 bg-light">
        <div class="container">
          <div class="row align-items-center">
            <div class="col-lg-6 mb-4 mb-lg-0">
              <div class="safety-image">
                <img src="@/assets/images/about-safety.jpg" alt="Safety Protocols" class="img-fluid rounded-lg shadow">
              </div>
            </div>
            <div class="col-lg-6">
              <h2 class="font-weight-bold mb-4">Safety & Sustainability</h2>
              <p>We prioritize your health and the planet. All our service providers follow strict safety protocols—sanitizing tools, wearing PPE, and adhering to local health guidelines. Your in-home appointments are conducted with utmost care for hygiene and comfort.</p>
              <p class="mb-4">Committed to greener beauty, we partner with brands that use eco-friendly packaging, responsibly sourced ingredients, and carbon-offset shipping. Every order you place supports our vision of a more sustainable future.</p>
              <b-button variant="primary" size="lg">Learn More About Our Practices</b-button>
            </div>
          </div>
        </div>
      </section>

      <!-- How It Works -->
      <section class="py-5">
        <div class="container">
          <div class="text-center mb-5">
            <h2 class="font-weight-bold">How to Use the App</h2>
            <p class="lead text-muted">Getting started with OVVURA is simple</p>
          </div>
          
          <div class="row">
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="step-card text-center p-4 h-100">
                <div class="step-number bg-primary text-white mb-3 mx-auto">1</div>
                <h4 class="font-weight-bold mb-3">Download the App</h4>
                <p>Available on iOS and Android. Search for OVVURA in the App Store or Google Play.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="step-card text-center p-4 h-100">
                <div class="step-number bg-primary text-white mb-3 mx-auto">2</div>
                <h4 class="font-weight-bold mb-3">Sign Up</h4>
                <p>Create an account in seconds using your email or phone number. Secure and password-protected.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="step-card text-center p-4 h-100">
                <div class="step-number bg-primary text-white mb-3 mx-auto">3</div>
                <h4 class="font-weight-bold mb-3">Browse Services</h4>
                <p>Explore categories like beauty, wellness, and healthcare. Filter by location, price, and ratings.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="step-card text-center p-4 h-100">
                <div class="step-number bg-primary text-white mb-3 mx-auto">4</div>
                <h4 class="font-weight-bold mb-3">Book Appointment</h4>
                <p>Select your preferred date, time, and professional. Confirm in one tap for instant booking.</p>
              </div>
            </div>
          </div>
          
          <div class="row justify-content-center">
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="step-card text-center p-4 h-100">
                <div class="step-number bg-primary text-white mb-3 mx-auto">5</div>
                <h4 class="font-weight-bold mb-3">Track Your Booking</h4>
                <p>Receive real-time updates on your provider's arrival time and appointment status.</p>
              </div>
            </div>
            
            <div class="col-md-6 col-lg-3 mb-4">
              <div class="step-card text-center p-4 h-100">
                <div class="step-number bg-primary text-white mb-3 mx-auto">6</div>
                <h4 class="font-weight-bold mb-3">Rate & Review</h4>
                <p>Share your feedback to help us maintain high standards and recommend the best professionals.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Legal Footer -->
      <section class="py-4 bg-dark text-white">
        <div class="container text-center">
          <p class="mb-0">OVVURA PORTAL L.L.C owns, maintains, and operates https://ovvura.ae/. United Arab Emirates is our country of domicile and all transactions are governed by UAE law.</p>
        </div>
      </section>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";

export default {
  components: { Layout },
  data() {
    return {
      // You can add any reactive data here if needed
    }
  }
}
</script>

<style scoped>
.about-page {
  overflow-x: hidden;
}

.about-hero {
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  padding: 6rem 0;
}

.hero-illustration {
  animation: float 6s ease-in-out infinite;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.icon-circle-lg {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.value-card {
  background: white;
  border-radius: 10px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.value-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.value-icon {
  transition: transform 0.3s ease;
}

.value-card:hover .value-icon {
  transform: scale(1.1);
}

.step-card {
  background: white;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.step-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.step-number {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
}

.why-list li {
  padding: 15px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.why-list li:hover {
  background: rgba(110, 142, 251, 0.1);
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
}

@media (max-width: 768px) {
  .about-hero {
    padding: 4rem 0;
    text-align: center;
  }
  
  .hero-illustration {
    margin-top: 2rem;
  }
}
</style>